import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { AuthService } from '../core/auth.service';

interface PropertyAdminItem {
  id: number;
  code: string;
  name: string;
  vmApiBaseUrl: string;
  isActive: boolean;
}

@Component({
  selector: 'app-property-administration',
  imports: [FormsModule],
  template: `
    <section class="card">
      <header class="head">
        <div>
          <h1>Property Administration</h1>
          <p class="muted">Search, create, update, and remove properties.</p>
        </div>
      </header>

      @if (!canManageProperties()) {
        <div class="error">Property administration access required.</div>
      } @else {
        @if (msg()) {
          <div class="ok" [class.error]="err()">{{ msg() }}</div>
        }

        <div class="tool-row top-actions">
          <label class="grow">Find Property
            <input [(ngModel)]="propertySearch" name="propertySearch" placeholder="Search by property code or name" (ngModelChange)="onPropertySearchChange()">
          </label>
          <button type="button" class="secondary" (click)="clearPropertySearch()" [disabled]="busy()">Clear Search</button>
          <button type="button" (click)="startNewProperty()" [disabled]="busy()">New Property</button>
        </div>

        @if (showSearchResults()) {
          <section class="results">
            @if (searchResults().length) {
              <div class="results-list">
                @for (p of searchResults(); track p.id) {
                  <button type="button" class="result-item" (click)="openEditProperty(p)" [disabled]="busy()">
                    <strong>{{ p.code }}</strong>
                    <span>{{ p.name }}</span>
                    <small>{{ p.vmApiBaseUrl }}</small>
                  </button>
                }
              </div>
            } @else {
              <div class="muted">No properties match this search.</div>
            }
          </section>
        }

        @if (editorOpen()) {
          <form class="editor" (submit)="saveEditor($event)">
            <h2>{{ editorMode() === 'new' ? 'New Property' : 'Edit Property' }}</h2>
            <div class="grid">
              <label>Property Code
                <input [(ngModel)]="editor.code" name="editorCode" required [readonly]="editorMode() === 'edit'">
              </label>
              <label>Property Name
                <input [(ngModel)]="editor.name" name="editorName" required>
              </label>
              <label>VM Login URL
                <select [(ngModel)]="editor.vmApiBaseUrl" name="editorVmApiBaseUrl">
                  <option value="https://login.vmcloudpms.com">https://login.vmcloudpms.com</option>
                  <option value="https://login.vmcloudpms.co.uk">https://login.vmcloudpms.co.uk</option>
                  <option value="https://development.vmcloudpms.com">https://development.vmcloudpms.com</option>
                </select>
              </label>
              <label class="inline">
                <input type="checkbox" [(ngModel)]="editor.isActive" name="editorIsActive">
                Active
              </label>
            </div>
            <div class="editor-actions">
              <button type="submit" [disabled]="busy()">{{ editorMode() === 'new' ? 'Create Property' : 'Save Property' }}</button>
              @if (editorMode() === 'edit' && isAdmin()) {
                <button type="button" class="danger" (click)="deleteEditor()" [disabled]="busy()">Delete</button>
              }
              <button type="button" class="secondary" (click)="cancelEditor()" [disabled]="busy()">Cancel</button>
            </div>
          </form>
        }
      }
    </section>
  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:14px;padding:16px;display:grid;gap:12px}
    .head{display:flex;justify-content:space-between;gap:12px;align-items:start;flex-wrap:wrap}
    h1{margin:0}
    .muted{margin:0;color:var(--muted)}
    .grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    label{display:grid;gap:6px;font-weight:600;color:#24334a}
    input,select{height:40px;border:1px solid #c8d2e1;border-radius:8px;padding:0 10px}
    .row-actions,.tool-row{display:flex;gap:8px;flex-wrap:wrap;align-items:end}
    .top-actions{align-items:flex-end}
    .results{border:1px solid #e3e8f2;border-radius:10px;padding:10px;background:#f9fbff}
    .results-list{display:grid;gap:8px}
    .result-item{display:grid;gap:2px;text-align:left;border:1px solid #d8e1ef;border-radius:8px;background:#fff;padding:8px 10px;color:#24334a;cursor:pointer}
    .result-item small{color:#64748b}
    .editor{display:grid;gap:12px;border:1px solid #e3e8f2;border-radius:10px;padding:12px;background:#fff}
    .editor h2{margin:0}
    .editor-actions{display:flex;gap:8px;flex-wrap:wrap}
    .inline{display:flex;align-items:center;gap:8px}
    .grow{flex:1}
    .error{color:#a42323;background:#fdecec;border:1px solid #f4c0c0;border-radius:8px;padding:8px 10px}
    .ok{color:#0f6f41;background:#e8f7ef;border:1px solid #bce8cf;border-radius:8px;padding:8px 10px}
    button{height:40px;border:0;border-radius:8px;background:#0f4bb8;color:#fff;font-weight:600;cursor:pointer;padding:0 12px}
    .secondary{background:#6b778c}
    .danger{background:#8a1f1f}
    @media (max-width: 900px){ .grid{grid-template-columns:1fr} }
    @media (max-width: 1100px){ .property-row{grid-template-columns:1fr} }
  `,
})
export class PropertyAdministrationComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  readonly canManageProperties = computed(() => ['admin', 'user'].includes(String(this.auth.user()?.role ?? '').toLowerCase()));
  readonly isAdmin = computed(() => String(this.auth.user()?.role ?? '').toLowerCase() === 'admin');
  readonly busy = signal(false);
  readonly msg = signal('');
  readonly err = signal(false);
  readonly searchResults = signal<PropertyAdminItem[]>([]);
  readonly showSearchResults = signal(false);
  readonly editorMode = signal<'new' | 'edit' | null>(null);
  readonly editorOpen = signal(false);
  private editingId: number | null = null;
  propertySearch = '';
  private propertySearchTimer: ReturnType<typeof setTimeout> | null = null;
  editor = {
    code: '',
    name: '',
    vmApiBaseUrl: 'https://login.vmcloudpms.com',
    isActive: true,
  };

  async ngOnInit(): Promise<void> {
    // Search-driven page: no initial property list render.
  }

  onPropertySearchChange(): void {
    if (this.propertySearchTimer) clearTimeout(this.propertySearchTimer);
    this.propertySearchTimer = setTimeout(() => {
      void this.runSearch();
    }, 180);
  }

  clearPropertySearch(): void {
    this.propertySearch = '';
    this.searchResults.set([]);
    this.showSearchResults.set(false);
  }

  async runSearch(): Promise<void> {
    if (!this.canManageProperties()) return;
    const qRaw = this.propertySearch.trim();
    if (!qRaw) {
      this.searchResults.set([]);
      this.showSearchResults.set(false);
      return;
    }
    this.busy.set(true);
    this.err.set(false);
    try {
      const q = encodeURIComponent(qRaw);
      const result = await firstValueFrom(
        this.api.get<{ items: PropertyAdminItem[]; total: number }>(
          `/properties?q=${q}&take=100&skip=0&includeInactive=true`,
        ),
      );
      this.searchResults.set(result.items || []);
      this.showSearchResults.set(true);
    } catch {
      this.err.set(true);
      this.msg.set('Unable to load properties.');
    } finally {
      this.busy.set(false);
    }
  }

  startNewProperty(): void {
    this.editorMode.set('new');
    this.editorOpen.set(true);
    this.editingId = null;
    this.editor = {
      code: '',
      name: '',
      vmApiBaseUrl: 'https://login.vmcloudpms.com',
      isActive: true,
    };
  }

  openEditProperty(item: PropertyAdminItem): void {
    this.editorMode.set('edit');
    this.editorOpen.set(true);
    this.editingId = item.id;
    this.editor = {
      code: item.code,
      name: item.name,
      vmApiBaseUrl: item.vmApiBaseUrl,
      isActive: item.isActive,
    };
  }

  cancelEditor(): void {
    this.editorMode.set(null);
    this.editorOpen.set(false);
    this.editingId = null;
  }

  async saveEditor(event: Event): Promise<void> {
    event.preventDefault();
    const mode = this.editorMode();
    if (!mode) return;
    this.busy.set(true);
    this.err.set(false);
    this.msg.set('');
    try {
      if (mode === 'new') {
        await firstValueFrom(
          this.api.post('/properties', {
            code: this.editor.code.trim(),
            name: this.editor.name.trim(),
            vmApiBaseUrl: this.editor.vmApiBaseUrl,
            isActive: this.editor.isActive,
          }),
        );
        this.msg.set('Property created.');
      } else if (this.editingId) {
        await firstValueFrom(
          this.api.put(`/properties/${this.editingId}`, {
            name: this.editor.name.trim(),
            vmApiBaseUrl: this.editor.vmApiBaseUrl,
            isActive: this.editor.isActive,
          }),
        );
        this.msg.set('Property updated.');
      }
      window.dispatchEvent(new Event('settings-changed'));
      if (this.propertySearch.trim()) {
        await this.runSearch();
      }
    } catch {
      this.err.set(true);
      this.msg.set(`Failed to ${mode === 'new' ? 'create' : 'update'} property.`);
    } finally {
      this.busy.set(false);
    }
  }

  async deleteEditor(): Promise<void> {
    if (this.editorMode() !== 'edit' || !this.editingId) return;
    const token = window.prompt(`Type DELETE ${this.editor.code} to permanently remove this property and all scoped data.`);
    if (!token) return;
    this.busy.set(true);
    this.err.set(false);
    this.msg.set('');
    try {
      await firstValueFrom(this.api.delete(`/properties/${this.editingId}`, { body: { confirmationText: token } }));
      this.msg.set(`Property ${this.editor.code} deleted.`);
      this.cancelEditor();
      if (this.propertySearch.trim()) {
        await this.runSearch();
      }
      window.dispatchEvent(new Event('settings-changed'));
    } catch {
      this.err.set(true);
      this.msg.set('Delete failed.');
    } finally {
      this.busy.set(false);
    }
  }
}
